"use client";
import { Menu, X, ChevronDown } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Link } from "@/i18n/routing";
import { useNavigation, type NavigationItem, type NavigationChild } from "../../hooks/useNavigation";
import { DropdownMenu } from "../ui/dropdown-menu";
import { MegaMenu } from "../ui/mega-menu";
import { isFeatureEnabled, NavigationFeatureKey } from "@/config/navigationFeatures";

export const Navigation: React.FC = () => {
  const navigationItems = useNavigation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu and dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Ignore clicks originating from mega menu portal
      const megaMenuElement = document.querySelector('[data-mega-menu]');
      if (megaMenuElement && megaMenuElement.contains(event.target as Node)) {
        return;
      }
      const navigationElement = document.querySelector('[data-navigation]');
      const mobileMenuElement = document.querySelector('[data-mobile-menu]');
      const mobileMenuButton = document.querySelector('[aria-label="Open menu"]');
      
      // Close mobile menu if clicking outside
      if (mobileMenuOpen) {
        if (mobileMenuElement && !mobileMenuElement.contains(event.target as Node) &&
            mobileMenuButton && !mobileMenuButton.contains(event.target as Node)) {
          setMobileMenuOpen(false);
        }
      }
      
      // Close all dropdowns if clicking outside navigation
      if (activeDropdown && navigationElement && !navigationElement.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen, activeDropdown]);

  const handleDropdownToggle = (itemId: string) => {
    // Close all other dropdowns first, then toggle current one
    if (activeDropdown !== itemId) {
      setActiveDropdown(itemId);
    } else {
      setActiveDropdown(null);
    }
  };

  const closeAllDropdowns = () => {
    setActiveDropdown(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    closeAllDropdowns();
  };

  // Check if item should use mega menu
  const shouldUseMegaMenu = (itemId: string) => {
    const allowedIds: NavigationFeatureKey[] = ['solutions', 'industries', 'resources', 'company'];
    const key = itemId as NavigationFeatureKey;
    return allowedIds.includes(key) && isFeatureEnabled(key);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden lg:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms] relative z-50" data-navigation>
        <div className="flex items-center gap-2 rtl:flex-row-reverse">
          {navigationItems.map((item: NavigationItem) => (
            <div key={item.id} className="nav-item">
              {item.hasDropdown && item.children ? (
                shouldUseMegaMenu(item.id) ? (
                  <MegaMenu
                    trigger={
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-md font-navigation font-[number:var(--navigation-font-weight)] text-white text-[length:var(--navigation-font-size)] leading-[var(--navigation-line-height)] tracking-[var(--navigation-letter-spacing)] [font-style:var(--navigation-font-style)] hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 cursor-pointer">
                        <span>{item.label}</span>
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    }
                    menuKey={item.id}
                    isOpen={activeDropdown === item.id}
                    onToggle={() => handleDropdownToggle(item.id)}
                    isScrolled={isScrolled}
                    menuItems={item.children as NavigationChild[] | undefined}
                  />
                ) : (
                  <DropdownMenu
                    trigger={
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-md font-navigation font-[number:var(--navigation-font-weight)] text-white text-[length:var(--navigation-font-size)] leading-[var(--navigation-line-height)] tracking-[var(--navigation-letter-spacing)] [font-style:var(--navigation-font-style)] hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 cursor-pointer">
                        <span>{item.label}</span>
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    }
                    items={item.children}
                    isOpen={activeDropdown === item.id}
                    onToggle={() => handleDropdownToggle(item.id)}
                    onClose={closeAllDropdowns}
                    isMobile={false}
                  />
                )
              ) : (
                <Link
                  href={item.href}
                  onClick={(e) => {
                    // Dispatch navigation start event
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('navigation-start', {
                        detail: { href: item.href }
                      }));
                    }
                  }}
                  className="flex items-center gap-1.5 rtl:flex-row-reverse px-3 py-2 rounded-md font-navigation font-[number:var(--navigation-font-weight)] text-white text-[length:var(--navigation-font-size)] leading-[var(--navigation-line-height)] tracking-[var(--navigation-letter-spacing)] [font-style:var(--navigation-font-style)] hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 cursor-pointer"
                >
                  <span>{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </div>
      </nav>

      {/* Mobile Menu Button */}
      <button 
        className="lg:hidden relative z-[9999] p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors duration-200 hover:text-blue-200 hover:bg-white/10 rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 transition-transform duration-200" />
        ) : (
          <Menu className="w-6 h-6 transition-transform duration-200" />
        )}
      </button>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm transition-opacity duration-300" onClick={closeMobileMenu}>
          
          {/* Mobile Menu Panel */}
          <div 
            data-mobile-menu
            className="fixed top-0 right-0 w-80 max-w-[90vw] h-full bg-slate-900 shadow-2xl transform transition-transform duration-300 ease-out translate-x-0 overflow-hidden z-[9999]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-center p-6 border-b border-slate-700/50">
                <h2 className="text-xl font-semibold text-white">Menu</h2>
              </div>

              {/* Mobile Menu Items */}
              <div className="flex-1 overflow-y-auto py-4 px-2">
                  {navigationItems.map((item: NavigationItem) => (
                    <div 
                      key={item.id} 
                      className="px-2 py-1"
                      style={{
                        animationDelay: `${navigationItems.indexOf(item) * 100}ms`
                      }}
                    >
                      {item.hasDropdown && item.children ? (
                        <DropdownMenu
                          trigger={
                            <div className="flex items-center gap-3 p-3 rounded-lg text-white hover:bg-slate-800 hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 group w-full cursor-pointer">
                              <span className="font-medium text-sm">{item.label}</span>
                              <ChevronDown className="w-4 h-4 ml-auto" />
                            </div>
                          }
                          items={item.children}
                          isOpen={activeDropdown === item.id}
                          onToggle={() => handleDropdownToggle(item.id)}
                          onClose={() => setActiveDropdown(null)}
                          isMobile={true}
                          className="w-full"
                        />
                      ) : (
                        <Link
                          href={item.href}
                          onClick={(e) => {
                            // Dispatch navigation start event
                            if (typeof window !== 'undefined') {
                              window.dispatchEvent(new CustomEvent('navigation-start', {
                                detail: { href: item.href }
                              }));
                            }
                            closeMobileMenu();
                          }}
                          className="flex items-center gap-3 p-3 rounded-lg text-white hover:bg-slate-800 hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 group w-full cursor-pointer"
                        >
                          <span className="font-medium text-sm">{item.label}</span>
                        </Link>
                      )}
                    </div>
                  ))}
              </div>

              {/* Mobile Menu Footer */}
              <div className="p-4 border-t border-slate-700/50 text-center text-gray-500 text-xs">
                &copy; {new Date().getFullYear()} CISCODE. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};